import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  anchorPointToBounds,
  getBestOverlayPlacement,
} from './useOverlayPlacement.js';

/**
 * @param {{ width: number; height: number }} a
 * @param {{ width: number; height: number }} b
 */
function sizeMatches(a, b) {
  return Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
}

/**
 * @param {{ x: number; y: number; width: number; height: number; placement: string } | null | undefined} a
 * @param {{ x: number; y: number; width: number; height: number; placement: string } | null | undefined} b
 */
function boundsMatch(a, b) {
  if (!a || !b) return false;
  return (
    a.placement === b.placement &&
    Math.abs(a.x - b.x) < 0.5 &&
    Math.abs(a.y - b.y) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

/**
 * @param {{
 *   anchor: { x: number; y: number };
 *   actions: Array<{
 *     key: string;
 *     title: string;
 *     onClick: () => void;
 *     Icon: import('react').ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
 *     disabled?: boolean;
 *     ariaExpanded?: boolean;
 *   }>;
 *   extraContent?: import('react').ReactNode;
 *   canvasBounds?: { x?: number; y?: number; width: number; height: number };
 *   blockedBounds?: Array<{ x: number; y: number; width: number; height: number }>;
 *   ariaLabel?: string;
 *   onBoundsChange?: (bounds: { x: number; y: number; width: number; height: number; placement: string }) => void;
 * }} props
 */
function SelectionActionMenu({
  anchor,
  actions,
  extraContent = null,
  canvasBounds = null,
  blockedBounds = [],
  ariaLabel = 'Object actions',
  onBoundsChange,
}) {
  const menuRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const onBoundsChangeRef = useRef(onBoundsChange);
  const reportedBoundsRef = useRef(
    /** @type {{ x: number; y: number; width: number; height: number; placement: string } | null} */ (null),
  );
  const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    onBoundsChangeRef.current = onBoundsChange;
  }, [onBoundsChange]);

  useLayoutEffect(() => {
    const node = menuRef.current;
    if (!node) return undefined;

    const measure = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      setMeasuredSize((prev) => {
        const next = { width: rect.width, height: rect.height };
        return sizeMatches(prev, next) ? prev : next;
      });
    };

    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [actions.length, extraContent]);

  const placement = useMemo(() => {
    if (!canvasBounds || measuredSize.width <= 0 || measuredSize.height <= 0) {
      return {
        placement: 'top',
        x: anchor.x,
        y: anchor.y - 48,
        score: 0,
      };
    }

    const anchorBounds = anchorPointToBounds(anchor);
    const bounds = {
      x: canvasBounds.x ?? 0,
      y: canvasBounds.y ?? 0,
      width: canvasBounds.width,
      height: canvasBounds.height,
    };

    return getBestOverlayPlacement({
      anchorBounds,
      overlaySize: measuredSize,
      canvasBounds: bounds,
      blockedBounds,
      preferredPlacements: ['top', 'bottom', 'right', 'left'],
      gap: 14,
    });
  }, [
    anchor.x,
    anchor.y,
    blockedBounds,
    canvasBounds?.height,
    canvasBounds?.width,
    canvasBounds?.x,
    canvasBounds?.y,
    measuredSize.height,
    measuredSize.width,
  ]);

  useLayoutEffect(() => {
    if (measuredSize.width <= 0 || measuredSize.height <= 0) return;
    const nextBounds = {
      x: placement.x,
      y: placement.y,
      width: measuredSize.width,
      height: measuredSize.height,
      placement: placement.placement,
    };
    if (boundsMatch(reportedBoundsRef.current, nextBounds)) return;
    reportedBoundsRef.current = nextBounds;
    onBoundsChangeRef.current?.(nextBounds);
  }, [
    measuredSize.height,
    measuredSize.width,
    placement.placement,
    placement.x,
    placement.y,
  ]);

  const pointerClass = `floor-plan-selection-action-menu__pointer floor-plan-selection-action-menu__pointer--${placement.placement}`;

  return (
    <div
      ref={menuRef}
      className="floor-plan-selection-action-menu"
      style={{ left: placement.x, top: placement.y }}
      role="toolbar"
      aria-label={ariaLabel}
    >
      <div className="floor-plan-selection-action-menu__body">
        {extraContent}
        <div className="floor-plan-selection-action-menu__actions">
          {actions.map(({ key, title, onClick, Icon, disabled, ariaExpanded }) => (
            <button
              key={key}
              type="button"
              className="floor-plan-designer-tool-icon-btn floor-plan-selection-action-menu__btn"
              onClick={onClick}
              title={title}
              disabled={disabled}
              aria-expanded={ariaExpanded}
            >
              <Icon size={16} aria-hidden />
            </button>
          ))}
        </div>
      </div>
      <span className={pointerClass} aria-hidden />
    </div>
  );
}

export default SelectionActionMenu;
